import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { asyncScheduler, BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, mergeMap, tap } from 'rxjs/operators';
import { AppState, RoleMgr, ShowRequest } from '../../core';
import { AppTab } from '../../core/app-tab';
import { activity_status, DriveTest, PathTest, ScoutPath, TaskType } from '../../core/models';
import { PathTestDoc } from '../../core/schema/path-test-schema';
import { ScoutTaskDoc } from '../../core/schema/scout-task-schema';
import { UserDoc } from '../../core/schema/user-schema';
import { BuildingService, PointService, RouteService, ScoutTaskService, TaskService, UserService } from '../../core/services';
import { PathTestService } from '../../core/services/path-test.service';
import { DriveTestService } from '../../core/services/drive-test.service';
import { PathTestEditComponent } from '../../path-test/path-test-edit/path-test-edit.component';
import { FesBaseComponent } from '../../shared/components/fes-base.component';
import { NumericCompareFn, SortCompareFn, SortEvent, StringArrayCompareFn } from '../../shared/components/sort-field/sort-field.component';
import { FilterFieldConfig, FilterFieldMode } from '../../shared/directives/filter-field.directive';
import { TaskEditComponent } from '../task-edit/task-edit.component';
import { diff } from 'deep-object-diff';
import Status = activity_status.Id
import { DriveTestDoc } from '../../core/schema/drive-test-schema';
import { ListViewDirectiveOf } from '../../shared/directives/list-view.directive';
import { TaskCalendarDateFilter } from '../task-calendar/task-calendar.component';
import { PageChangedEvent } from 'ngx-bootstrap/pagination';

const zeroPad = (floor:string) => {
  const match = floor.match(/^(\d+)(.*)/)
  return match ? match[1].padStart(3,'0') + match[2] : floor
}

const buildFloorData = (floors:string[]) => Array.from(new Set(floors)).filter(f=>f).map(floor=>
    `${floor}`.replace(/^0+([0-9])/,'$1')).map(floor=>({trimmed:floor,padded:zeroPad(floor)})).sort((a,b)=>SortCompareFn(a.padded,b.padded))


export class TaskRow {
  schedule: string
  stage: string
  type: string
  buildingId?: string
  pathIds?: string[]
  groupingfield?:string[]
  floorNames: string[]
  sortFloorNames: string[]

  assignedUserId: string
  statusName: activity_status.Name
  modifiedByUserId: string | null
  modifiedDate: string
}

class PathTestRow extends TaskRow {
  order: number
  cycle: number
  pointIds: string[]
}
class DriveTestRow extends TaskRow {
  cycle: number
  routeId: string
}

class ScoutRow extends TaskRow {
  pathIdAndNumberArray: { id: string, pathNumber: number }
}

@Component({
  selector: 'app-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TaskListComponent extends FesBaseComponent implements OnInit, OnDestroy  {

  tasks : Array<ScoutTaskDoc|PathTestDoc|DriveTestDoc> = []

  isLoading : boolean = false;
  filterObj : { [key:string]: string | FilterFieldConfig } = {}
  bShowCalendar = false

  // For notifying task calendar of changes
  taskUpdated$ = new Subject<(PathTestDoc|ScoutTaskDoc|DriveTestDoc)>()

  @Input('tabConfig') set setTabConfig( tabConfig : AppTab ) {
    tabConfig.setSelected = this.onTabSelected
    tabConfig.viewInfo.matches = ( showInfo : ShowRequest ) => {
      try {
        const requestFilter = JSON.stringify(JSON.parse(showInfo.id)?.filter)?.toLowerCase() || "{}"
        const currentFilter = JSON.stringify(this.filterObj)?.toLowerCase()
        return requestFilter == currentFilter
      } catch ( e : any ) {
        console.error(e)
        return false
      }
    }
    this.tabConfig = tabConfig
  }
  tabConfig: AppTab

  @Input('detailFn') public detailFn : (vi: ShowRequest) => void;

  @Input() set contextJson( strJson: string) {
    try {
      console.log(`set contextJson() : ${strJson}`)
      if ( strJson ) {
        const context = JSON.parse(strJson)
        this.lastContext = context
        if ( context?.filter ) {
          this.filterObj = context.filter
        }
        if ( context?.sort ) {
          // TODO restore sort order from browser URL
        }
        this.type$.next((this.filterObj?.type instanceof Object) ? this.filterObj.type.value : this.filterObj.type)
      }
    } catch ( error ) {
      this.taskService.publishError(error,this)
    }
  }
  lastContext :any = {
  }

  @Output("contextJsonChange") contextChangeEmitter = new EventEmitter<string>();

  @ViewChild('fesListView', { read: ListViewDirectiveOf}) fesListView : ListViewDirectiveOf<
    (PathTestDoc|ScoutTaskDoc|DriveTestDoc), (PathTestDoc|ScoutTaskDoc|DriveTestDoc)[]>;

  constructor(private taskService: TaskService,
              public pathTestService: PathTestService,
              public driveTestService: DriveTestService,
              public scoutTaskService: ScoutTaskService,
              private buildingService: BuildingService,
              private routeService: RouteService,
              private pointService: PointService,
              private userService: UserService,
              private bsModalService: BsModalService,
              public roleMgr: RoleMgr,
              public appState : AppState, private datePipe: DatePipe ) {
    super(appState);
    this.pageSize = parseInt(localStorage.getItem('preferences.tasklist.pageSize')??'25');
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.destroy()
  }

  userNames : { [key:string]: string } = {}
  buildingNames : { [key:string]: string } = {}
  routeNames : { [key:string]: string } = {}
  pathTestPointInfoByPointId : {[key:string]: {floorName: string}} = {}
  driveTestPointInfoByPointId : {[key:string]: {floorName: string}} = {}

  type$ = new BehaviorSubject<string>('')
  viewScouts$ = new BehaviorSubject<boolean>(false)
  viewTests$ = new BehaviorSubject<boolean>(false)
  viewDrives$ = new BehaviorSubject<boolean>(false)
  typeChanged$ = new EventEmitter<string|FilterFieldConfig>()

  bViewScouts : boolean = true
  bViewTests : boolean = true
  bViewDrives : boolean = true

  initialize() {
    this.taskService.doWhenReady( () => {

      this.subscribe(this.type$, (type:string) => {
        type = type?.toLowerCase() || '' // decide tab title from "type" filter
        const bScoutMatched = 'scout'.indexOf(type) >= 0
        const bTestMatched = 'test'.indexOf(type) >= 0
        const bDriveMatched = 'drive'.indexOf(type) >= 0
        const bNoMatch = !type || (!bScoutMatched && !bTestMatched && !bDriveMatched)
        this.viewScouts$.next( bNoMatch || bScoutMatched)
        this.viewTests$.next( bNoMatch || bTestMatched)
        this.viewDrives$.next( bNoMatch || bDriveMatched)
        let tabTitle = "Tasks"
        if ( this.viewScouts$.value && !this.viewTests$.value && !this.viewDrives$.value) {
          tabTitle = "Scouts"
        } else if ( this.viewTests$.value && !this.viewScouts$.value && !this.viewDrives$.value) {
          tabTitle = "Indoor Tests"
        } else if (this.viewDrives$.value && !this.viewTests$.value && !this.viewScouts$.value) {
          tabTitle = "Drive Tests"
        }
        this.bViewScouts = this.viewScouts$.value
        this.bViewTests = this.viewTests$.value
        this.bViewDrives = this.viewDrives$.value
        this.tabConfig.viewInfo.title.next(tabTitle)
      })
      this.subscribe(this.typeChanged$.pipe(debounceTime(500)), type => this.type$.next(type))

      this.subscribe(this.userService.rxCollection.find().$, users => {
        this.userNames = {}
        for( const userDoc of users ) {
          this.userNames[userDoc._id] = userDoc.firstName + ' ' + userDoc.lastName
        }
      })

      this.subscribe(this.buildingService.rxCollection.find().$, buildings => {
        this.buildingNames = {}
        for( const buildingDoc of buildings ) {
          this.buildingNames[buildingDoc.id] = buildingDoc.commonName
        }
      })

      this.subscribe(this.routeService.rxCollection.find().$, routes => {
        this.routeNames = {}
        for( const routeDoc of routes ) {
          this.routeNames[routeDoc.id] = routeDoc.market
        }
      })

      // cache anything related to points we might need (just floors of path tests for now)
      const pointsCacheForPathTests$ = this.viewTests$.pipe( distinctUntilChanged(), mergeMap( viewTests => viewTests ?
          this.pointService.rxCollection.find().$ : of([])
      ))

      this.subscribe(pointsCacheForPathTests$, points => {
        this.pathTestPointInfoByPointId = {}
        points.forEach(p=> {
          this.pathTestPointInfoByPointId[p._id] = { floorName: p.floorName }
        })
        for ( const task of this.tasks ) {
          if ( task.type == TaskType.PATH_TEST ) {
            const row = task['row'] as PathTestRow
            this.rebuildPathTestInfo(row)
          }
          if ( task.type == TaskType.DRIVE_TEST ) {
            const row = task['row'] as PathTestRow
            this.rebuildPathTestInfo(row)
          }
        }
      })


      let scoutQuery = this.scoutTaskService.rxCollection.find().where({"type": TaskType.SCOUT })
      let pathTestQuery = this.pathTestService.rxCollection.find().where({"type": TaskType.PATH_TEST })
      let driveTestQuery = this.driveTestService.rxCollection.find().where({"type": TaskType.DRIVE_TEST })

      if ( !this.roleMgr.canEditUsers && !this.roleMgr.canSchedule ){
        const addUserContext = (scoutQuery) => scoutQuery.where({
          $or: [
            { "assignments.scheduleUserId": { $eq: (""+this.appState.getUser()?._id) } },
            { "assignments.testUserId": { $eq: (""+this.appState.getUser()?._id) } }
          ]
        })
        scoutQuery = addUserContext(scoutQuery)
        pathTestQuery = addUserContext(pathTestQuery)
        driveTestQuery = addUserContext(driveTestQuery)
      }

      const scouts$ = this.viewScouts$.pipe( distinctUntilChanged(),
          tap( viewScouts=> { if ( viewScouts ) this.isLoading = true }),
          mergeMap( viewScouts => viewScouts ? scoutQuery.$.pipe(tap(scoutDocs=>this.updateScoutRows(scoutDocs))) : of([])
          ))
      const pathTests$ = this.viewTests$.pipe( distinctUntilChanged(),
          tap( viewTests=> { if ( viewTests ) this.isLoading = true }),
          mergeMap( viewTests => viewTests ?
              pathTestQuery.$.pipe(tap(testDocs=>this.updatePathTestRows(testDocs))) : of([])
          ))
      const driveTests$ = this.viewDrives$.pipe( distinctUntilChanged(),
          tap( viewDrives=> { if ( viewDrives ) this.isLoading = true }),
          mergeMap( viewDrives => viewDrives ?
              driveTestQuery.$.pipe(tap(testDocs=>this.updatePathTestRows(testDocs))) : of([])
          ))

      const tasks$ = combineLatest([scouts$,pathTests$, driveTests$]).pipe(
          map( ([scoutDocs,pathTestDocs,driveTestDocs]) => [...scoutDocs,...pathTestDocs,...driveTestDocs] )
      )

      this.subscribe( tasks$, async tasks => {
        try {
          tasks.sort( (lhsTask,rhsTask)=> {
            const lhsRow = lhsTask['row']
            const rhsRow = rhsTask['row']
            return SortCompareFn(lhsRow.schedule?.scheduledDate,rhsRow.schedule?.scheduledDate)
                || SortCompareFn(lhsRow.buildingId,rhsRow.buildingId)
                || SortCompareFn(lhsRow.routeId,rhsRow.routeId)
                || SortCompareFn(lhsRow.groupingfield,rhsRow.groupingfield)
                //|| SortCompareFn(`${lhsRow.pathIds}`,`${rhsRow.pathIds}`)
                || NumericCompareFn(lhsRow.cycle,rhsRow.cycle)
                || NumericCompareFn(lhsRow.order,rhsRow.order)
                // Floor names may arrive later from point table so cannot sort on them immediately
                || StringArrayCompareFn(lhsRow.floorNames,rhsRow.floorNames)
          })
          this.tasks = tasks
          // this.tasksById = this.tasks.reduce( (map,task) => {
          //   map[task._id] = task
          //   return map
          // }, {});
        } catch ( error ) {
          this.taskService.publishError(error,this)
        } finally {
          this.isLoading = false
        }
      })
    })
  }

  //tasksById: { [id:string]: ScoutTaskDoc | PathTestDoc | DriveTestDoc } = {}
  bTabSelected = false
  onTabSelected = ( bSelected : boolean ) => {
    console.log('task-list tab selected: ' + bSelected )
    this.bTabSelected = bSelected
    if ( bSelected ) {
      this.initialize()
    } else {
      this.unsubscribe();
      this.tasks = [];
      //this.tasksById = {};
      this.pageableTasks = [];
    }
  }


  formatScheduledDate = ( strDate ) => {
    return this.datePipe.transform(strDate,'yyyy-MM-dd')
  }


  dumpTaskToConsole(task : ScoutTaskDoc | PathTestDoc ) {
    console.log(task.toJSON())
  }

  unsubscribe() {
    console.log('unsubscribe()')
    super.unsubscribe()
    for( let task of this.tasks ) {
      const subscriptionNames = ['docSubscription']
      subscriptionNames.forEach( subscriptionName => {
        task[subscriptionName]?.unsubscribe()
        delete task[subscriptionName]
      })
    }
  }


  updatePathTestRows( tasks : PathTestDoc[] | DriveTestDoc[]) {
    console.log(`updatePathTestRows() count: ${tasks.length}`)
    for ( let i = 0; i < tasks.length; i++ ) {
      const task = tasks[i];
      //const existingTask = this.tasksById[task._id];
      //if ( !existingTask || existingTask._rev !== task._rev) {
        this.updateTaskRow( task );
      //}
    }
  }

  updateScoutRows( tasks : ScoutTaskDoc[]) {
    console.log(`updateScoutRows() count: ${tasks.length}`)
    for ( let i = 0; i < tasks.length; i++ ) {
      const task = tasks[i]
      this.updateTaskRow( task )
    }
  }

  // for sort and filtering
  assignedUserNameResolver = ( task : ScoutTaskDoc|PathTestDoc|DriveTestDoc, fieldName : string ) : string => {
    const userId = task['row']?.assignedUserId
    return this.userNames[userId]
  }
  buildingNameResolver = ( task : ScoutTaskDoc|PathTestDoc, fieldName : string ) : string => {
    const id = task['row']?.buildingId
    return this.buildingNames[id]
  }
  routeNameResolver = ( task : DriveTestDoc, fieldName : string ) : string => {
    const id = task['row']?.routeId
    return this.routeNames[id]
  }
  modifiedDateResolver  = ( task : ScoutTaskDoc|PathTestDoc|DriveTestDoc, fieldName : string ) : string => {
    const row = task['row']
    const modifiedDate = row?.modifiedDate
    const val = modifiedDate ? this.datePipe.transform(modifiedDate,'yyyy-MM-dd HH:mm:ss ') : ''
    // append user name to end of date so date and/or user can be entered in filter
    return val + this.userNames[row.modifiedByUserId]
  }


  updateTaskRow( task : ScoutTaskDoc | PathTestDoc |  DriveTestDoc ) {

    let row: Partial<PathTestRow|ScoutRow> = task['row']
    if ( !row ) {
      row = task['row'] = {}
    }

    if ( !row['docSubscription'] ) {

      task['docSubscription']?.unsubscribe()
      task['docSubscription'] = task.$.subscribe( taskDoc => {
        row.schedule = taskDoc.schedule
        row.stage = taskDoc.stage
        row.type = taskDoc.type
        row.buildingId = taskDoc.buildingId

        if ( task.type == TaskType.PATH_TEST ) {
          const pathTestRow = row as PathTestRow
          pathTestRow.cycle = taskDoc.cycle

          pathTestRow.assignedUserId = task.assignedUserId
          const _data = task._data
          pathTestRow.order = _data['order']
          pathTestRow.pathIds = [ _data.pathId ]
          const testPoints = _data['testPoints']
          pathTestRow.pointIds = testPoints?.map(tp=>tp.pointId)
          this.rebuildPathTestInfo(pathTestRow)
          const pathTestStatusId = PathTest.statusFromPoints(testPoints)
          pathTestRow.statusName = activity_status.Names[pathTestStatusId]

          // TBD - Need pathTask modifiedTime/userId and update it on any change of document?
          // For now interpret modifiedTime as the last sequence run time
          // (could also include seq.data.timestamp)
          const [,pathTestLatestSeq] = this.pathTestService.findLatestTestPointAndSeq(testPoints)

          const haveStartTime =  !!pathTestLatestSeq?.startTime
          pathTestRow.modifiedDate = pathTestLatestSeq?.startTime || '' // space on null makes empty oldest in sort
          pathTestRow.modifiedByUserId = haveStartTime ? pathTestLatestSeq?.userId : null

        }else if ( task.type == TaskType.SCOUT ) {
          const scoutRow = row as ScoutRow

          scoutRow.assignedUserId = task.assignments?.testUserId

          const scoutPaths = task._data['scoutPaths']
          try{
            if ( !scoutPaths ) {
              throw new Error(`Scout task ${task._id} is missing scoutPaths data.`)
            }
            ScoutPath.sortByPathNumber(scoutPaths)
            scoutRow.pathIds = scoutPaths.map(path=>path.pathNumber)
            scoutRow.pathIdAndNumberArray = scoutPaths.map(path=>({id:path.id,pathNumber:path.pathNumber}))
            const floorData = buildFloorData(scoutPaths.map(p=>p.floor))
            scoutRow.floorNames = floorData.map(fd=>fd.trimmed)
            scoutRow.sortFloorNames = floorData.map(fd=>fd.padded)

            if ( scoutPaths.length > 0 ) {

              const pathStatusIds = scoutPaths.map(x => x.statusId)
              const TaskStatusIds = [
                Status.COMPLETE,
                Status.FIELD_COMPLETE,
                Status.REVIEW_COMPLETE,
                Status.NOT_USING,
                Status.ARCHIVED
              ]
              let scoutStatusId = null
              for ( const taskStatusId of TaskStatusIds){
                if(pathStatusIds.every(id=>id==taskStatusId)){
                  scoutStatusId = taskStatusId
                  break
                }
              }
              if ( !scoutStatusId ) {
                if ( pathStatusIds.includes(id=>id==Status.COMPLETE||id==Status.NOT_USING) ) {
                  scoutStatusId = Status.COMPLETE
                }
              }
              scoutRow.statusName = activity_status.Names[scoutStatusId]||activity_status.Names.IN_PROGRESS

              const { date, userId } = scoutPaths.map(path=>{return {date:path.modifiedDate,userId:path.userId}})
                  .sort((a,b)=>a?.date.localeCompare(b?.date)).pop()
              scoutRow.modifiedDate = date
              scoutRow.modifiedByUserId = userId
            } else {
              scoutRow.statusName = activity_status.Names.NOT_STARTED
              scoutRow.modifiedDate = ''
              scoutRow.modifiedByUserId = null
            }
          } catch ( error ) {
            scoutRow.statusName = activity_status.Names.NOT_STARTED
            scoutRow.modifiedDate = ''
            scoutRow.modifiedByUserId = null
            console.error(error)
            this.taskService.publishError(error,this)
          }
        }  else if ( task.type == TaskType.DRIVE_TEST ) {
          const driveTestRow = row as DriveTestRow
          driveTestRow.cycle = taskDoc.cycle
          driveTestRow.routeId = taskDoc.routeId
          driveTestRow.assignedUserId = task.assignedUserId
          const _data = task._data
          const testPoints = _data['testPoints']
          const [,driveTestLatestSeq] = this.driveTestService.findLatestTestPointAndSeq(testPoints)
          const haveStartTime =  !!driveTestLatestSeq?.startTime
          driveTestRow.modifiedDate = driveTestLatestSeq?.startTime || '' // space on null makes empty oldest in sort
          driveTestRow.modifiedByUserId = haveStartTime ? driveTestLatestSeq?.userId : null
        }
        this.taskUpdated$.next(task)
      })
    }
  }

  // workaround for PathTest task docs not having floors (have to look up from points to find the floors but
  // points db has over 3000 records and finding points for each task was getting too slow)
  rebuildPathTestInfo(row: PathTestRow) {
    const ptInfos = row.pointIds?.map(pointId=>this.pathTestPointInfoByPointId[pointId]).filter(pi=>pi)
    if ( !ptInfos ) {
      row.floorNames = []
      row.sortFloorNames = []
      return
    }
    const floorNames = ptInfos.map(pi=>pi.floorName)
    const floorData = buildFloorData(floorNames)
    row.floorNames = floorData.map(floorData=>floorData.trimmed)
    row.sortFloorNames = floorData.map(floorData=>floorData.padded)
  }

  showBuilding = ( taskDoc : PathTestDoc|ScoutTaskDoc|DriveTestDoc) => {

    this.detailFn( new ShowRequest('tab', 'building', taskDoc.buildingId, taskDoc) )
  }
  showRoute = ( taskDoc : PathTestDoc|ScoutTaskDoc|DriveTestDoc) => {

    this.detailFn( new ShowRequest('tab', 'route', taskDoc.routeId, taskDoc) )
  }

  showPath = async ( taskDoc : PathTestDoc|ScoutTaskDoc|DriveTestDoc, pathId : string) => {
    if ( taskDoc.type == TaskType.PATH_TEST ) {
      this.detailFn( new ShowRequest('tab','path', JSON.stringify({pathId, buildingId: taskDoc?.buildingId}) ))
    } else if ( taskDoc.type == TaskType.SCOUT ) {
      this.detailFn( new ShowRequest('tab','scout path', pathId, taskDoc) )
    }else if ( taskDoc.type == TaskType.DRIVE_TEST ) {
      this.detailFn( new ShowRequest('tab','drive path', pathId, taskDoc) )
    }
  }

  editTask = async ( taskDoc : PathTestDoc|ScoutTaskDoc|DriveTestDoc) => {
    if ( taskDoc.type == TaskType.PATH_TEST ) {
      /*Commenting out Schedule view on Task list until we figure out what to do with Site Acq role*/
      // if(this.roleMgr.canSchedule) {
      //   tabType = 'schedule task'
      // }
      this.detailFn( new ShowRequest('tab', 'path test', taskDoc._id, taskDoc) )
    }else if ( taskDoc.type == TaskType.DRIVE_TEST ) {
      /*Commenting out Schedule view on Task list until we figure out what to do with Site Acq role*/
      // if(this.roleMgr.canSchedule) {
      //   //tabType = 'schedule task'
      //   const scoutTaskDoc = await this.scoutTaskService.getById(task._id)
      //   this.editScoutTaskModal(scoutTaskDoc)
      // }
      this.detailFn( new ShowRequest('tab','drive test', taskDoc._id, taskDoc) )
    } else if ( taskDoc.type == TaskType.SCOUT ) {
      /*Commenting out Schedule view on Task list until we figure out what to do with Site Acq role*/
      // if(this.roleMgr.canSchedule) {
      //   //tabType = 'schedule task'
      //   const scoutTaskDoc = await this.scoutTaskService.getById(task._id)
      //   this.editScoutTaskModal(scoutTaskDoc)
      // }
      this.detailFn( new ShowRequest('tab','scout task', taskDoc._id, taskDoc) )
    }
  }

  public editAssignedUserModal : BsModalRef
  allUsers : UserDoc[]
  selectedTask : ScoutTaskDoc | PathTestDoc | DriveTestDoc
  public selectedUser : UserDoc

  editAssignedUser = async ( template: TemplateRef<any>, pathTestRow : PathTest|DriveTest ) => {

    this.selectedTask = this.tasks.find(t=>t._id==pathTestRow._id)
    this.isLoading = true;
    try {
      this.allUsers = await this.userService.rxCollection.find().sort( {'firstName':'asc','lastName':'asc'}).exec()
      this.selectedUser = this.allUsers.find(u=>this.selectedTask.assignedUserId==`${u._id}`)
    } catch ( error ) {
      this.userService.publishError(error,this)
      return
    } finally {
      this.isLoading = false
    }
    this.editAssignedUserModal = this.bsModalService.show(template);
  }

  async saveAssignedUser() {
    try {
      console.log(JSON.stringify(this.selectedTask.toJSON()))
      const userId = this.selectedUser?._id ? this.selectedUser._id : ''
      if ( this.selectedTask._id ) {
        await this.selectedTask.atomicPatch({assignedUserId: `${userId}`, assignments: { testUserId: `${userId}`,
            scheduleUserId: `${this.selectedTask.assignments.scheduleUserId}`}})
      } else {
        this.selectedTask.assignedUserId = userId
        await this.selectedTask.save()
      }
      this.selectedTask=null;
      this.editAssignedUserModal.hide()
    } catch (error) {
      this.pathTestService.publishError(error,this)
    } finally {
    }
  }

  editPathTest( pathTestDoc : PathTestDoc | DriveTestDoc ) {
    let initialState;
    if(pathTestDoc.type === "drive test") {
      initialState = {
        pathTestDoc,
        userNamesById: this.userNames,
        routeNamesById: this.routeNames
      }
    }else if(pathTestDoc.type === "path test") {
      initialState = {
        pathTestDoc,
        userNamesById: this.userNames,
        buildingNamesById: this.buildingNames
      }
    }
    this.bsModalService.show(PathTestEditComponent, {ignoreBackdropClick: true, initialState});
  }

  editScoutTaskModal( scoutTaskDoc: ScoutTaskDoc ) {
    const initialState = {
    }
    const modalRef = this.bsModalService.show(TaskEditComponent, {
      ignoreBackdropClick: true,
      class: 'modal-lg',
      initialState
    });
    const taskEditComponent: TaskEditComponent = modalRef.content
    if ( !scoutTaskDoc ) {
      scoutTaskDoc = this.scoutTaskService.rxCollection.newDocument()
      scoutTaskDoc.type = 'scout'
    }
    taskEditComponent.setTaskDoc(scoutTaskDoc)
  }


  calendarDateFilter : {pattern:string, mode: FilterFieldMode} = null
  calendarColumnName : 'scheduledDate' | 'modifiedDate'

  toggleShowCalendar( columnName: 'scheduledDate' | 'modifiedDate' ) {
    console.log(`toggleShowCalendar(${columnName})`)
    const lastColumnName = this.calendarColumnName
    this.calendarColumnName = columnName

    if ( !lastColumnName ) {
      this.bShowCalendar = true
    } else if ( lastColumnName == columnName ) {
      this.bShowCalendar = !this.bShowCalendar
    } else if ( this.bShowCalendar ) {
      // clear previous column filter when switching to new column
      if ( lastColumnName ) {
        this.filterObj[lastColumnName] = {
          mode: 'contains',
          value: ''
        }
      }
    }

    if ( this.bShowCalendar ) {
      const getFieldVal = ( f ) => (f instanceof Object) ? f.value : f as string
      const fieldFilter = this.filterObj[columnName];
      let fieldVal = getFieldVal(fieldFilter);

      if ( !fieldVal ) {
        // no date is in the filter input so use set filter to the max month found in collection
        const dateExtractor = columnName == 'scheduledDate' ?
          (doc) => doc?.row.schedule?.scheduledDate :
          (doc) => doc?.row.modifiedDate

        // Prefer setting month to newest date in exiting filtered list
        // If no dates in the filtered view, use the entire task list to find newest
        const tasks = this.fesListView._viewList.some(d=>dateExtractor(d)) ? this.fesListView._viewList : this.tasks
        if ( tasks.length ) {
          const maxMonthDoc = tasks.reduce( (p,c)=>
              SortCompareFn(dateExtractor(p),dateExtractor(c))>0?p:c, tasks[0])
          let date = dateExtractor(maxMonthDoc);
          if ( date ) {
            const strYMD = columnName == 'modifiedDate' ?
              this.datePipe.transform(date,'yyyy-MM-dd') :
              this.datePipe.transform(date,'yyyy-MM-dd','UTC');
            fieldVal = strYMD;
            this.filterObj[columnName] = {
              mode: 'contains',
              value: strYMD.replace(/-\d{2}$/,'')
            }
          }
        }
      }
      //console.log(`toggleShowCalendar() filterObj: ${JSON.stringify(this.filterObj)}`)
      this.calendarDateFilter = {
        mode: 'contains',
        pattern: fieldVal
      }
      //console.log(`toggleShowCalendar(${columnName}) ==> ${JSON.stringify(this.calendarDateFilter)}`)
      asyncScheduler.schedule(this.fesListView.filter)
    } else {
        this.calendarDateFilter = null
    }
  }

  async closeCalendar( closeOpts: { clearFilter: boolean }){
    console.log(`closeCalendar(closeOpts:${JSON.stringify(closeOpts)})`)
    //const dateFilterField = this.dateFilterField
    this.bShowCalendar = false
    const columnName = this.calendarColumnName
    this.calendarColumnName = null
    if ( columnName ) {
      asyncScheduler.schedule( () => {
        if ( closeOpts.clearFilter ) {
          this.filterObj[columnName] = {
            mode: 'contains',
            value: ''
          }
          this.calendarDateFilter = null
          asyncScheduler.schedule( this.fesListView.filter)
          //dateFilterField.applyFilter( calendarDateFilter )
        }
      },50)
    }
  }

  onDateFilterChanged( filter: TaskCalendarDateFilter ) {
    console.log(`onDateFilterChanged(${JSON.stringify(filter)})`)
    this.filterObj[this.calendarColumnName] = {
      mode: filter.mode,
      value: filter.pattern
    }
    asyncScheduler.schedule( this.fesListView.filter)
  }

  //
  // Some adhoc paging to help with performance (long list slows everything down)
  //
  pageableTasks: Array<(ScoutTaskDoc|PathTestDoc|DriveTestDoc)&{row?:any}> = [];
  page = 1;
  pageSize = 25;
  totalPages: number;
  onPageNumberChanged(pageChangeEvent: PageChangedEvent ){
    console.log(`onPageNumberChanged()`,{pageChangeEvent});
  }
  onNumPagesChanged(numPages:number){
    asyncScheduler.schedule(()=>{
      this.totalPages = numPages;
    });
  }
  onPageSizeChange = (event: Event): void => {
    const selectElement = event.target as HTMLSelectElement;
    this.pageSize = parseInt((event.target as any).value);
    localStorage.setItem('preferences.tasklist.pageSize', `${this.pageSize}`)
    asyncScheduler.schedule(()=>{
      this.page = 1;
    });
  }


  onFiltered(list: Array<ScoutTaskDoc|PathTestDoc|DriveTestDoc>) {
    console.log(`onFiltered(${list?.length})`)
    this.pageableTasks = [...list];
    const bChanged = Object.keys(diff(this.lastContext.filter,this.filterObj)).length > 0
    const filter = {...this.filterObj}
    this.lastContext = { ...this.lastContext, filter }
    if ( bChanged ) {
      console.log(`onFiltered() context changed`)
      this.page = 1;
      this.contextChangeEmitter.next( JSON.stringify(this.lastContext))
    }
  }

  onSorted( event: {criteria: SortEvent, list: Array<ScoutTaskDoc|PathTestDoc|DriveTestDoc>}) {
    //console.log(`onSorted(criteria:${JSON.stringify(event?.criteria)},list:${event?.list?.length})`)
    //note... this gets called during filtering and sorting (filter cause implicit sort)
    this.pageableTasks = [...event.list];
    const bChanged = Object.keys(diff(this.lastContext.sort,event.criteria)).length > 0
    const sort = {...event.criteria}
    this.lastContext = { ...this.lastContext, sort }
    if ( bChanged ) {
      console.log(`onSorted() context changed`)
      this.page = 1;
      this.contextChangeEmitter.next( JSON.stringify(this.lastContext))
    }
  }

}
